import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getAuth, signInAnonymously } from "firebase/auth";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { selectCurrentUser } from "../../store/user/user.selector";
import { storage } from "../../utils/firebase/firebase";

import MissingSellerPage from "./MissingSellerPage";
import InvalidSellerPage from "./InvalidSellerPage";
import PublicCatalogLoadingPage from "./PublicCatalogLoadingPage";
import PublicCatalogHero from "./components/PublicCatalogHero";
import ProductCatalogCard from "./ProductCatalogCard";
import CartSidebar from "./CartSidebar";
import {
    getPublicPersonalizedCatalogProducts,
    getPublicSellerBySid,
} from "./publicPersonalized.service";

import Swal from "sweetalert2";

import { createPublicPersonalizedOrder } from "./publicPersonalized.service";
import { trackEvent } from "../../utils/analycts";

function moneyBRL(v) {
    return Number(v || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
}

function generateOrderCode() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const rand = Math.random().toString(36).substring(2, 7).toUpperCase();

    return `PP-${year}${month}${day}-${rand}`;
}

async function sendOrderTracking(payload) {
    try {
        const result = await createPublicPersonalizedOrder(payload);
        return result;
    } catch (error) {
        console.log("Falha ao registrar pedido:", error);
        return {
            ok: false,
            status: 500,
            message: "Falha ao registrar pedido.",
            data: null,
        };
    }
}

function sanitizeFileNamePart(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9-_]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase();
}

function getFileExtension(file) {
    const originalName = String(file?.name || "");
    const byName = originalName.includes(".")
        ? originalName.split(".").pop()
        : "";

    if (byName) {
        return byName.toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
    }

    const mime = String(file?.type || "").toLowerCase();

    if (mime.includes("png")) return "png";
    if (mime.includes("webp")) return "webp";
    if (mime.includes("jpeg") || mime.includes("jpg")) return "jpg";
    if (mime.includes("gif")) return "gif";

    return "jpg";
}

function buildAnalyticsItems(items = []) {
    return items.map((item, index) => ({
        item_id: String(item?.code || item?.id || `item-${index + 1}`),
        item_name: item?.productName || item?.name || "Produto personalizado",
        item_brand: "Pitaya",
        item_category: item?.productType || item?.chainType || "Personalizado",
        item_variant: item?.color || "",
        price: Number(item?.price || 0),
        quantity: Number(item?.quantity || 1),
        index,
    }));
}

async function ensurePublicFirebaseAuth() {
    const auth = getAuth();

    if (auth.currentUser) {
        return auth.currentUser;
    }

    if (typeof auth.authStateReady === "function") {
        await auth.authStateReady();
    }

    if (auth.currentUser) {
        return auth.currentUser;
    }

    const credential = await signInAnonymously(auth);

    return credential.user;
}

async function uploadOrderItemPhoto({ file, orderCode, item, itemIndex }) {
    if (!file) return null;

    const ext = getFileExtension(file);
    const safeOrderCode = sanitizeFileNamePart(orderCode || "pedido");
    const safeCode = sanitizeFileNamePart(item?.code || `item-${itemIndex + 1}`);
    const filename = `${safeOrderCode}-${safeCode}-${String(itemIndex + 1).padStart(2, "0")}.${ext}`;
    const storagePath = `pedidospersonalizados/${safeOrderCode}/${filename}`;

    const fileRef = ref(storage, storagePath);
    await uploadBytes(fileRef, file);

    const downloadUrl = await getDownloadURL(fileRef);

    return {
        filename,
        storagePath,
        downloadUrl,
        contentType: file?.type || "",
        originalName: file?.name || filename,
    };
}

async function prepareOrderItemsWithUploads(cartItems, orderCode) {
    const preparedItems = [];

    for (let index = 0; index < cartItems.length; index += 1) {
        const item = cartItems[index];
        let uploadedPhoto = null;

        if (item?.selectedPhoto) {
            uploadedPhoto = await uploadOrderItemPhoto({
                file: item.selectedPhoto,
                orderCode,
                item,
                itemIndex: index,
            });
        }

        preparedItems.push({
            ...item,
            photoName: uploadedPhoto?.filename || item?.photoName || "",
            photoOriginalName: uploadedPhoto?.originalName || item?.photoName || "",
            photoUrl: uploadedPhoto?.downloadUrl || "",
            photoStoragePath: uploadedPhoto?.storagePath || "",
            photoContentType: uploadedPhoto?.contentType || "",
            hasPhoto: Boolean(uploadedPhoto?.downloadUrl),
            selectedPhoto: undefined,
        });
    }

    return preparedItems;
}

export default function PublicPersonalizedPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const currentUser = useSelector(selectCurrentUser);

    const [loading, setLoading] = useState(true);
    const [seller, setSeller] = useState(null);
    const [invalidSeller, setInvalidSeller] = useState(false);
    const [products, setProducts] = useState([]);
    const [cartItems, setCartItems] = useState([]);
    const [customer, setCustomer] = useState({ name: "", phone: "" });
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [closeOptionsSignal, setCloseOptionsSignal] = useState(0);

    const pageViewTrackedRef = useRef(false);
    const invalidSellerTrackedRef = useRef(false);
    const missingSidTrackedRef = useRef(false);

    const [viewportWidth, setViewportWidth] = useState(
        typeof window !== "undefined" ? window.innerWidth : 1280
    );

    const isMobile = viewportWidth <= 640;

    useEffect(() => {
        function handleResize() {
            setViewportWidth(window.innerWidth);
        }

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        document.documentElement.classList.add("public-personalized-html");
        document.body.classList.add("public-personalized-body");

        return () => {
            document.documentElement.classList.remove("public-personalized-html");
            document.body.classList.remove("public-personalized-body");
        };
    }, []);

    const sidFromUrl = String(searchParams.get("sid") || "").trim();

    const effectiveSid = useMemo(() => {
        if (sidFromUrl) return sidFromUrl;
        if (currentUser?.uid && !currentUser?.isAnonymous) return currentUser.uid;
        return "";
    }, [currentUser, sidFromUrl]);

    useEffect(() => {
        if (!effectiveSid && !missingSidTrackedRef.current) {
            missingSidTrackedRef.current = true;

            trackEvent("public_personalized_missing_sid", {
                page_path: window?.location?.pathname || "",
                page_location: window?.location?.href || "",
            });
        }
    }, [effectiveSid]);

    useEffect(() => {
        let isMounted = true;

        async function loadPage() {
            if (!effectiveSid) {
                if (!isMounted) return;
                setLoading(false);
                setSeller(null);
                setInvalidSeller(false);
                return;
            }

            setLoading(true);
            setInvalidSeller(false);

            try {
                const sellerResponse = await getPublicSellerBySid(effectiveSid);

                if (!isMounted) return;

                if (!sellerResponse?.ok || !sellerResponse?.data) {
                    setSeller(null);
                    setInvalidSeller(true);
                    setProducts([]);
                    setLoading(false);
                    return;
                }

                setSeller(sellerResponse.data);

                const productsResponse = await getPublicPersonalizedCatalogProducts();

                if (!isMounted) return;

                const loadedProducts = productsResponse?.data || [];

                console.log("prodsss here : ", loadedProducts);
                setProducts(loadedProducts);
                setInvalidSeller(false);

                trackEvent("public_personalized_products_loaded", {
                    seller_id: String(sellerResponse?.data?.seller_id || ""),
                    seller_name: sellerResponse?.data?.name || sellerResponse?.data?.nome || "",
                    items_count: loadedProducts.length,
                });
            } catch (error) {
                console.log("Erro ao carregar página pública:", error);
                if (!isMounted) return;
                setSeller(null);
                setProducts([]);
                setInvalidSeller(true);

                trackEvent("public_personalized_load_error", {
                    seller_sid: String(effectiveSid || ""),
                    message: error?.message || "load_error",
                });
            } finally {
                if (isMounted) setLoading(false);
            }
        }

        loadPage();

        return () => {
            isMounted = false;
        };
    }, [effectiveSid]);

    useEffect(() => {
        if (!loading && seller && !invalidSeller && !pageViewTrackedRef.current) {
            pageViewTrackedRef.current = true;

            trackEvent("page_view", {
                page_title: "Catálogo Público Personalizados",
                page_path: window?.location?.pathname || "",
                page_location: window?.location?.href || "",
                seller_id: String(seller?.seller_id || ""),
                seller_name: seller?.name || seller?.nome || "",
                items_count: products.length,
            });

            trackEvent("view_item_list", {
                item_list_id: "public_personalized_catalog",
                item_list_name: "Catálogo Público Personalizados",
                seller_id: String(seller?.seller_id || ""),
                items: buildAnalyticsItems(products),
            });

            trackEvent("public_personalized_page_view", {
                seller_id: String(seller?.seller_id || ""),
                seller_name: seller?.name || seller?.nome || "",
                items_count: products.length,
            });
        }
    }, [loading, seller, invalidSeller, products]);

    useEffect(() => {
        if (!loading && invalidSeller && !invalidSellerTrackedRef.current) {
            invalidSellerTrackedRef.current = true;

            trackEvent("public_personalized_invalid_seller", {
                seller_sid: String(effectiveSid || ""),
                page_path: window?.location?.pathname || "",
            });
        }
    }, [loading, invalidSeller, effectiveSid]);

    const total = useMemo(() => {
        return cartItems.reduce((acc, item) => {
            return acc + Number(item.price || 0) * Number(item.quantity || 1);
        }, 0);
    }, [cartItems]);

    const productsGridStyle = useMemo(() => {
        if (isMobile) {
            return {
                ...styles.productsGrid,
                gridTemplateColumns: "repeat(2, calc((100% - 12px) / 2))",
                gap: 12,
            };
        }

        return {
            ...styles.productsGrid,
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
        };
    }, [isMobile]);

    const pageBgStyle = useMemo(() => {
        return {
            ...styles.pageBg,
            padding: isMobile ? "14px 8px 90px" : "24px 14px 90px",
        };
    }, [isMobile]);

    const containerStyle = useMemo(() => {
        return {
            ...styles.container,
            gap: isMobile ? 14 : 18,
        };
    }, [isMobile]);

    const gridHeaderStyle = useMemo(() => {
        return {
            ...styles.gridHeader,
            marginTop: isMobile ? 2 : 6,
        };
    }, [isMobile]);

    const gridTitleStyle = useMemo(() => {
        return {
            ...styles.gridTitle,
            fontSize: isMobile ? 20 : 24,
        };
    }, [isMobile]);

    const gridSubStyle = useMemo(() => {
        return {
            ...styles.gridSub,
            marginTop: isMobile ? 4 : 6,
            fontSize: isMobile ? 13 : 14,
        };
    }, [isMobile]);

    function handleOpenCart(origin = "unknown") {
        setIsCartOpen(true);

        trackEvent("begin_checkout", {
            currency: "BRL",
            value: Number(total || 0),
            items: buildAnalyticsItems(cartItems),
        });

        trackEvent("public_personalized_open_cart", {
            origin,
            cart_items_count: cartItems.length,
            total_value: Number(total || 0),
            seller_id: String(seller?.seller_id || ""),
        });
    }

    function handleCloseCart(origin = "unknown") {
        setIsCartOpen(false);

        trackEvent("public_personalized_close_cart", {
            origin,
            cart_items_count: cartItems.length,
            total_value: Number(total || 0),
            seller_id: String(seller?.seller_id || ""),
        });
    }

    function showAddedToCart(item) {
        Swal.fire({
            title: "Produto adicionado!",
            width: 560,
            icon: "success",
            padding: "24px",
            confirmButtonText: "Ver carrinho",
            cancelButtonText: "Continuar comprando",
            showCancelButton: true,
            confirmButtonColor: "#902348",
            cancelButtonColor: "#BDBDBD",
            willOpen: () => {
                setCloseOptionsSignal((prev) => prev + 1);
            },
            willClose: () => {
                setCloseOptionsSignal((prev) => prev + 1);
            },
            html: `
                <div style="text-align:left">
                    <div style="font-size:17px;font-weight:700;margin-bottom:4px;color:#111827">
                        ${item.productName || "-"}
                    </div>

                    ${item.description ? `
                        <div style="font-size:12px;color:#6b7280;margin-bottom:10px;line-height:1.45">
                            ${item.description}
                        </div>
                    ` : ""}

                    ${item.customText ? `
                        <div style="font-size:14px;color:#555;margin-bottom:4px">
                            Nome/Personalização: <b>${item.customText}</b>
                        </div>
                    ` : ""}

                    <div style="font-size:14px;color:#555;margin-bottom:4px">
                        Código: ${item.code || "-"}
                    </div>

                    <div style="font-size:14px;color:#555;margin-bottom:4px">
                        Cor: ${item.color || "-"}
                    </div>

                    <div style="font-size:14px;color:#555;margin-bottom:10px">
                        Tipo: ${item.productType || item.chainType || "-"}
                    </div>

                    <div style="font-size:18px;font-weight:700;color:#902348">
                        ${moneyBRL(item.price || 0)}
                    </div>
                </div>
            `,
        }).then((result) => {
            if (result.isConfirmed) {
                handleOpenCart("swal_after_add");
            }
        });
    }

    function handleAddToCart(item) {
        setCartItems((prev) => [...prev, item]);
        setCloseOptionsSignal((prev) => prev + 1);

        trackEvent("add_to_cart", {
            currency: "BRL",
            value: Number(item?.price || 0) * Number(item?.quantity || 1),
            items: buildAnalyticsItems([item]),
        });

        trackEvent("public_personalized_add_to_cart", {
            seller_id: String(seller?.seller_id || ""),
            item_code: String(item?.code || ""),
            item_name: item?.productName || "",
            item_category: item?.productType || item?.chainType || "",
            color: item?.color || "",
            has_photo: Boolean(item?.selectedPhoto),
            quantity: Number(item?.quantity || 1),
            value: Number(item?.price || 0),
        });

        showAddedToCart(item);
    }

    function handleRemoveItem(indexToRemove) {
        const removedItem = cartItems[indexToRemove];

        setCartItems((prev) => prev.filter((_, index) => index !== indexToRemove));

        if (removedItem) {
            trackEvent("remove_from_cart", {
                currency: "BRL",
                value: Number(removedItem?.price || 0) * Number(removedItem?.quantity || 1),
                items: buildAnalyticsItems([removedItem]),
            });

            trackEvent("public_personalized_remove_from_cart", {
                seller_id: String(seller?.seller_id || ""),
                item_code: String(removedItem?.code || ""),
                item_name: removedItem?.productName || "",
                quantity: Number(removedItem?.quantity || 1),
                value: Number(removedItem?.price || 0),
            });
        }
    }

    async function handleGenerateOrder() {
        if (!cartItems.length) {
            trackEvent("public_personalized_order_validation_error", {
                reason: "empty_cart",
                seller_id: String(seller?.seller_id || ""),
            });
            return;
        }

        const customerName = String(customer?.name || "").trim();
        const phoneDigits = String(customer?.phone || "").replace(/\D/g, "");

        if (!customerName) {
            trackEvent("public_personalized_order_validation_error", {
                reason: "missing_name",
                seller_id: String(seller?.seller_id || ""),
                cart_items_count: cartItems.length,
                total_value: Number(total || 0),
            });

            Swal.fire({
                icon: "warning",
                title: "Informe seu nome",
                text: "Preencha seu nome antes de gerar o pedido.",
                confirmButtonColor: "#902348",
            });
            return;
        }

        if (!phoneDigits) {
            trackEvent("public_personalized_order_validation_error", {
                reason: "missing_phone",
                seller_id: String(seller?.seller_id || ""),
                cart_items_count: cartItems.length,
                total_value: Number(total || 0),
            });

            Swal.fire({
                icon: "warning",
                title: "Informe seu WhatsApp",
                text: "Preencha seu telefone antes de gerar o pedido.",
                confirmButtonColor: "#902348",
            });
            return;
        }

        if (phoneDigits.length < 10) {
            trackEvent("public_personalized_order_validation_error", {
                reason: "invalid_phone",
                seller_id: String(seller?.seller_id || ""),
                phone_length: phoneDigits.length,
                cart_items_count: cartItems.length,
                total_value: Number(total || 0),
            });

            Swal.fire({
                icon: "warning",
                title: "WhatsApp inválido",
                text: "Digite um número de WhatsApp válido com DDD.",
                confirmButtonColor: "#902348",
            });
            return;
        }

        const orderCode = generateOrderCode();
        const createdAt = new Date();
        const itemsWithPhotoCount = cartItems.filter(
            (item) => Boolean(item?.selectedPhoto)
        ).length;

        trackEvent("public_personalized_generate_order_clicked", {
            seller_id: String(seller?.seller_id || ""),
            cart_items_count: cartItems.length,
            total_value: Number(total || 0),
            items_with_photo_count: itemsWithPhotoCount,
        });

        Swal.fire({
            title: "Gerando seu pedido...",
            html: "Estamos preparando as informações e enviando as fotos para você.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });

        let stage = "initializing";

        try {
            let firebaseUser = getAuth().currentUser;

            if (itemsWithPhotoCount > 0) {
                stage = "firebase_auth";
                firebaseUser = await ensurePublicFirebaseAuth();
            }

            trackEvent("public_personalized_order_upload_started", {
                seller_id: String(seller?.seller_id || ""),
                order_code: orderCode,
                cart_items_count: cartItems.length,
                total_value: Number(total || 0),
                items_with_photo_count: itemsWithPhotoCount,
                firebase_auth_state: firebaseUser
                    ? firebaseUser.isAnonymous
                        ? "anonymous"
                        : "authenticated"
                    : "none",
            });

            stage = itemsWithPhotoCount > 0 ? "storage_upload" : "prepare_items";

            const preparedItems = await prepareOrderItemsWithUploads(cartItems, orderCode);

            stage = "order_api";

            const payload = {
                order_code: orderCode,
                seller_id: seller?.seller_id,
                customer: {
                    ...customer,
                    name: customerName,
                    phone: customer.phone,
                },
                items: preparedItems,
                total,
                created_at: createdAt.toISOString(),
            };

            const result = await sendOrderTracking(payload);

            if (!result?.ok) {
                throw new Error(result?.message || "Não foi possível salvar o pedido.");
            }

            stage = "completed";

            trackEvent("purchase", {
                transaction_id: orderCode,
                currency: "BRL",
                value: Number(total || 0),
                items: buildAnalyticsItems(preparedItems),
            });

            trackEvent("public_personalized_order_success", {
                order_code: orderCode,
                seller_id: String(seller?.seller_id || ""),
                cart_items_count: preparedItems.length,
                total_value: Number(total || 0),
                items_with_photo_count: preparedItems.filter((item) => Boolean(item?.hasPhoto)).length,
            });

            Swal.close();

            navigate(
                `/produtos-personalizados/pedido-gerado${seller?.seller_id ? `?sid=${seller.seller_id}` : ""}`,
                {
                    state: {
                        orderCode,
                        createdAt,
                        seller,
                        customer: {
                            ...customer,
                            name: customerName,
                            phone: customer.phone,
                        },
                        items: preparedItems,
                        total,
                        payload,
                        savedOrder: result?.data || null,
                        showSuccessModal: true,
                    },
                }
            );
        } catch (error) {
            console.log("Erro ao gerar pedido com upload das fotos:", error);

            const firebaseUser = getAuth().currentUser;

            trackEvent("public_personalized_order_error", {
                seller_id: String(seller?.seller_id || ""),
                order_code: orderCode,
                cart_items_count: cartItems.length,
                total_value: Number(total || 0),
                items_with_photo_count: itemsWithPhotoCount,
                stage,
                error_code: error?.code || "",
                message: error?.message || "order_error",
                firebase_auth_state: firebaseUser
                    ? firebaseUser.isAnonymous
                        ? "anonymous"
                        : "authenticated"
                    : "none",
            });

            Swal.fire({
                icon: "error",
                title: "Erro ao finalizar pedido",
                text:
                    error?.message ||
                    "Não foi possível salvar o pedido ou enviar as imagens agora. Tente novamente.",
                confirmButtonColor: "#902348",
            });
        }
    }

    if (loading) return <PublicCatalogLoadingPage />;
    if (!effectiveSid) return <MissingSellerPage />;
    if (invalidSeller || !seller) return <InvalidSellerPage />;

    return (
        <div style={pageBgStyle}>
            <div style={containerStyle}>
                <PublicCatalogHero seller={seller} />

                <div style={gridHeaderStyle}>
                    <div style={gridTitleStyle}>Escolha seu modelo</div>
                    <div style={gridSubStyle}>
                        Selecione o produto, personalize e monte seu pedido.
                    </div>
                    <div style={styles.gridSbstileDelivery}>
                        <span style={styles.gridSbstileDeliveryTopic}>Prazo de entrega:</span> em até 35 dias corridos da confirmação do pedido!
                    </div>
                </div>

                <div style={productsGridStyle}>
                    {[...products]
                        .sort((a, b) => {
                            const aOrder = Number(a.ordering || 0);
                            const bOrder = Number(b.ordering || 0);

                            if (aOrder !== bOrder) return aOrder - bOrder;

                            return String(a.pitaya_code || "").localeCompare(
                                String(b.pitaya_code || "")
                            );
                        })
                        .map((product) => (
                            <ProductCatalogCard
                                key={product.id}
                                product={product}
                                onAddToCart={handleAddToCart}
                                closeOptionsSignal={closeOptionsSignal}
                                isMobile={isMobile}
                            />
                        ))}
                </div>
            </div>

            {!!cartItems.length && (
                <button
                    type="button"
                    onClick={() => handleOpenCart("floating_button")}
                    style={styles.cartFab}
                >
                    Gerar pedido
                    {cartItems.length > 0 && (
                        <span style={styles.cartBadge}>{cartItems.length}</span>
                    )}
                </button>
            )}

            <div
                style={{
                    ...styles.cartOverlay,
                    ...(isCartOpen ? styles.cartOverlayOpen : {}),
                }}
                onClick={() => handleCloseCart("overlay_click")}
            >
                <div
                    style={{
                        ...styles.cartPanel,
                        ...(isCartOpen ? styles.cartPanelOpen : {}),
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={styles.cartHeader}>
                        <div style={styles.cartHeaderTitle}>Seu pedido</div>
                        <button
                            type="button"
                            onClick={() => handleCloseCart("header_button")}
                            style={styles.closeBtn}
                        >
                            Fechar
                        </button>
                    </div>

                    <CartSidebar
                        items={cartItems}
                        customer={customer}
                        setCustomer={setCustomer}
                        total={total}
                        onRemoveItem={handleRemoveItem}
                        onGenerateOrder={handleGenerateOrder}
                        seller={seller}
                    />
                </div>
            </div>
        </div>
    );
}

const styles = {
    pageBg: {
        minHeight: "100vh",
        width: "100%",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
        background: "linear-gradient(180deg, rgba(144,35,72,0.08) 0px, #f6f7fb 260px, #f6f7fb 100%)",
        padding: "24px 14px 90px",
        boxSizing: "border-box",
    },
    container: {
        width: "100%",
        maxWidth: 1240,
        margin: "0 auto",
        display: "grid",
        gap: 18,
        boxSizing: "border-box",
    },
    gridHeader: {
        marginTop: 6,
    },
    gridTitle: {
        fontSize: 24,
        fontWeight: 900,
        color: "#111827",
        lineHeight: 1.1,
    },
    gridSub: {
        marginTop: 6,
        fontSize: 14,
        fontWeight: 600,
        color: "#4b5563",
        lineHeight: 1.35,
    },
    productsGrid: {
        display: "grid",
        alignItems: "stretch",
    },
    cartFab: {
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 60,
        border: "none",
        borderRadius: 999,
        padding: "14px 18px",
        background: "#902348",
        color: "#fff",
        fontWeight: 900,
        boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
        cursor: "pointer",
    },
    cartBadge: {
        marginLeft: 8,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 22,
        height: 22,
        borderRadius: 999,
        background: "#fff",
        color: "#902348",
        fontWeight: 900,
        fontSize: 12,
        padding: "0 6px",
    },
    cartOverlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0)",
        pointerEvents: "none",
        transition: "all 220ms ease",
        zIndex: 80,
    },
    cartOverlayOpen: {
        background: "rgba(0,0,0,0.38)",
        pointerEvents: "auto",
    },
    cartPanel: {
        position: "absolute",
        right: 0,
        top: 0,
        height: "100%",
        width: "100%",
        maxWidth: 430,
        background: "#fff",
        transform: "translateX(100%)",
        transition: "transform 240ms ease",
        boxShadow: "-10px 0 30px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
    },
    cartPanelOpen: {
        transform: "translateX(0)",
    },
    cartHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 14,
        borderBottom: "1px solid rgba(17,24,39,0.08)",
    },
    cartHeaderTitle: {
        fontWeight: 900,
        fontSize: 18,
        color: "#111827",
    },
    closeBtn: {
        border: "1px solid rgba(17,24,39,0.12)",
        borderRadius: 10,
        background: "#fff",
        padding: "8px 10px",
        fontWeight: 800,
        cursor: "pointer",
    },
    gridSbstileDelivery:{
        fontSize: 12,
        color: 'grey',
        fontWeight: 800,
        marginTop: 5,
        fontStyle: 'italic'
    },
    gridSbstileDeliveryTopic: {
        color: 'black',
    }
};